import {
	panoramaClientFactory,
	PanoramaPlatform,
	type PanoramaClient,
} from '@wix/fe-essentials-viewer-platform/panorama-client'
import { getMonitoringClientFunction } from '@wix/monitoring-browser-sdk-host'
import type { AppMonitoringPanoramaConfig, HostMonitoringContext } from '@wix/monitoring-types'
import {
	addRerouteDataToSentryEvent,
	getPanoramaGlobalConfig,
	isSSR,
	isSentryEventFromNonWixTpa,
} from '@wix/thunderbolt-commons'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { BrowserWindowSymbol, SiteFeatureConfigSymbol, type AppWithMonitoring } from '@wix/thunderbolt-symbols'
import { name } from './symbols'
import type { AppMonitoringApi, AppMonitoringSiteConfig } from './types'

const getHostMonitoringContext = ({
	appId,
	metaSiteId,
	siteUrl,
}: {
	appId: string
	metaSiteId?: string
	siteUrl?: string
}): HostMonitoringContext => ({
	appId,
	platform: 'SITE_VIEWER',
	tenantType: 'SITE',
	tenantId: metaSiteId,
	siteUrl,
})

const getPanoramaConfig = ({ app, componentId }: { app?: AppWithMonitoring; componentId?: string }) => {
	if (!app || !componentId) {
		return undefined
	}

	const externalId = app.externalIdByComponentId?.[componentId]

	if (!externalId) {
		return undefined
	}

	return app.panoramaConfigByArtifactId?.[externalId]
}

const createPanoramaClient = ({
	app,
	componentId,
	panoramaConfig,
	isSsr,
}: {
	app: AppWithMonitoring
	componentId: string
	panoramaConfig: AppMonitoringPanoramaConfig
	isSsr: boolean
}): PanoramaClient => {
	const { groupId, artifactId, fingerprint } = panoramaConfig.panoramaOptions.project

	return panoramaClientFactory({
		data: { isSsr },
		baseParams: {
			platform: PanoramaPlatform.Viewer,
			appDefId: app.appId,
			componentId,
			fullArtifactId: `${groupId}.${artifactId}`,
			artifactVersion: fingerprint,
		},
	})
		.withGlobalConfig(getPanoramaGlobalConfig())
		.client()
}

const initPanoramaClients = ({ isSsr }: { isSsr: boolean }) => {
	const panoramaClients = new Map<string, PanoramaClient>()

	const setPanoramaClient = ({
		app,
		componentId,
		panoramaConfig,
	}: {
		app: AppWithMonitoring
		componentId: string
		panoramaConfig: AppMonitoringPanoramaConfig
	}) => {
		try {
			panoramaClients.set(componentId, createPanoramaClient({ app, componentId, panoramaConfig, isSsr }))
		} catch (e) {
			console.error('[AppMonitoring] Failed to build panorama client for component', componentId, e)
		}
	}

	const getPanoramaClient = ({
		app,
		componentId,
		panoramaConfig,
	}: {
		app?: AppWithMonitoring
		componentId?: string
		panoramaConfig?: AppMonitoringPanoramaConfig
	}) => {
		// `componentId` is required for panoramaClient as panorama reports are scoped per artifact and component.
		if (!app || !componentId || !panoramaConfig) {
			return undefined
		}

		if (!panoramaClients.has(componentId)) {
			setPanoramaClient({ app, componentId, panoramaConfig })
		}

		return panoramaClients.get(componentId)
	}

	return {
		getPanoramaClient,
	}
}

/**
 * This is a feature.
 * You can get your configuration from site-assets and viewer-model injected into your feature
 */
export const AppMonitoring = withDependencies<AppMonitoringApi>(
	[named(SiteFeatureConfigSymbol, name), BrowserWindowSymbol],
	(siteFeatureConfig: AppMonitoringSiteConfig, window) => {
		const getMonitoringConfig = (appId: string) =>
			siteFeatureConfig.appsWithMonitoring.find((app) => app.appId === appId)

		const { getPanoramaClient } = initPanoramaClients({ isSsr: isSSR(window) })

		const resolvePanoramaClient = ({ appId, componentId }: { appId: string; componentId?: string }) => {
			const app = getMonitoringConfig(appId)

			const panoramaConfig = getPanoramaConfig({ app, componentId })

			const panoramaClient = getPanoramaClient({ app, componentId, panoramaConfig })

			return {
				app,
				panoramaConfig,
				panoramaClient,
			}
		}

		return {
			featureName: name,
			addRerouteDataToSentryEvent,
			getMonitoringClientFunction: ({ appId, componentId, metaSiteId, siteUrl, getAuthToken }) => {
				const { app, panoramaConfig, panoramaClient } = resolvePanoramaClient({ appId, componentId })

				// Apps may have multiple monitoring components, but only Panorama configurations can be retrieved using a component's externalId.
				// For other cases, a single app-level monitoring config is used.
				const monitoringConfig = panoramaConfig ?? app?.monitoringComponent?.monitoring

				return getMonitoringClientFunction({
					monitoringConfig,
					hostContext: getHostMonitoringContext({ appId, metaSiteId, siteUrl }),
					sentrySDK: window.Sentry,
					sentryTransport: siteFeatureConfig.sentryTransport,
					fetch: globalThis.fetch,
					getAuthToken,
					baseUrl: siteUrl,
					panoramaClient,
				})
			},
			getComponentLoadReporter: ({ appId, componentId }) => {
				const { panoramaClient } = resolvePanoramaClient({ appId, componentId })

				if (!panoramaClient) {
					return undefined
				}

				return {
					reportLoadStart: ({ transactionId }) => {
						panoramaClient.reportLoadStart(undefined, { id: transactionId })
					},
					reportLoadFinish: ({ transactionId }) => {
						panoramaClient.reportLoadFinish(undefined, { id: transactionId })
					},
				}
			},
			getMonitoringConfig,
			isSentryEventFromNonWixTpa,
		}
	}
)
